// src/encounter/assign-care-plan.tsx
//
// [PROD-3206] The physician assigns a care plan (Samer, 18 Oct Cohorts decisions; Andrew approved).
// Signal level: block · FHIR: the server's CarePlan, created by the care model's first activity.
//
// "Assign care plan" in the A&P Plan area → a dialog to pick a care model → Confirm → C2
// (`POST /workflows/care-models/{id}/enrolments { patientId }`). The app's token carries its own client_id, so the
// server records the enrolment as made from the practitioner app; nothing extra is sent.
//
// Rules held here:
//   · Behind `?x=assign-care-plan` (the parent mounts it only then). OFF = no button, no call.
//   · Every published care model is offered. A model whose `enrolmentChannel` is reception (or unset) is MARKED,
//     never hidden and never refused (Workflows #177: data only).
//   · alreadyEnrolled → "Already on {model} since {date}." and nothing is created. The reply names the first
//     enroller by user id only, so no name is shown.
//   · A failure is a sentence in the dialog; a lost answer or a 5xx is "not confirmed", never "not assigned".
//     A 409 (an overlapping run) reads like alreadyEnrolled. Only a 422 shows the server's own text.
//   · UI text is US English (enrolls, enrollment, organization).
//   · A separate block, not a verb on CarePlanReadout: the readout stays a readout (design desk D-14).
//
// Pieces: Signal DS Button (the DS `add` icon: src/encounter is lucide-free by lint, PROD-1599), Modal (size md, the same as the cohort enrol dialog), Select, HelperText, Banner.
import { useState } from 'react';
import { toast } from 'sonner';
import { Banner, Button, cn, HelperText, Icon, Modal, Select, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import type { CareModelOption } from './lib/care-model-options';
import { alreadyOnLine, describeAssignError } from './lib/assign-care-plan-text';
import { useAssignCarePlan, useCareModelOptions } from './services/use-assign-care-plan';

export const RECEPTION_MARK = 'Usually enrolled by reception';
export const RECEPTION_NOTE = 'Reception usually enrolls patients in this care model. You can still assign it.';

function optionHint(model: CareModelOption): string | undefined {
  if (model.channel === 'reception') return model.description ? `${RECEPTION_MARK} · ${model.description}` : RECEPTION_MARK;
  return model.description ?? undefined;
}

export interface AssignCarePlanDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
}

export function AssignCarePlanDialog(props: AssignCarePlanDialogProps) {
  if (!props.open) return null;
  return <AssignCarePlanForm {...props} />;
}

function AssignCarePlanForm({ onClose, patientId }: AssignCarePlanDialogProps) {
  const models = useCareModelOptions(true);
  const assign = useAssignCarePlan(patientId);
  const [chosenId, setChosenId] = useState<string>('');
  const [already, setAlready] = useState<string | null>(null);

  const chosen = models.data?.find((m) => m.definitionId === chosenId) ?? null;
  const busy = assign.isPending;

  function choose(id: string) {
    if (busy) return;
    assign.reset();
    setAlready(null);
    setChosenId(id);
  }

  function confirm() {
    if (!chosen || busy) return;
    assign.mutate(chosen.definitionId, {
      onSuccess: (data) => {
        if (data.alreadyEnrolled) {
          setAlready(alreadyOnLine(chosen.name, data.enrolledOn));
          return;
        }
        toast.success(
          data.carePlanId
            ? `Care plan assigned: ${chosen.name}.`
            : `Care plan assigned: ${chosen.name}. The plan shows here once the care model creates it.`,
        );
        onClose();
      },
    });
  }

  const footer = already ? (
    <Button type="button" variant="secondary" onClick={onClose}>
      Done
    </Button>
  ) : (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button type="button" onClick={confirm} disabled={busy || !chosen}>
        {busy ? 'Assigning…' : 'Confirm'}
      </Button>
    </>
  );

  return (
    <Modal open onClose={busy ? () => undefined : onClose} title="Assign a care plan" size="md" footer={footer}>
      <div className="flex flex-col gap-4">
        {models.isSuccess && models.data.length === 0 ? (
          <p className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.secondary)}>No published care model is available for your organization.</p>
        ) : (
          <Select
            label="Care model"
            required
            placeholder={models.isPending ? 'Loading care models…' : 'Choose a care model'}
            options={(models.data ?? []).map((m) => {
              const hint = optionHint(m);
              return { value: m.definitionId, label: m.name, ...(hint ? { hint } : {}) };
            })}
            value={chosenId || undefined}
            onChange={choose}
            disabled={busy || models.isPending || models.isError}
            error={models.isError ? 'Care models could not be loaded. Close and try again.' : undefined}
          />
        )}
        {chosen?.channel === 'reception' && !already ? <HelperText>{RECEPTION_NOTE}</HelperText> : null}
        {already ? (
          <Banner variant="info" title={already}>
            No change made. No new care plan was created.
          </Banner>
        ) : null}
        {assign.isError ? (
          <HelperText tone="error" role="alert">
            {describeAssignError(assign.error, chosen?.name ?? 'this care model')}
          </HelperText>
        ) : null}
      </div>
    </Modal>
  );
}

/** The action in the Plan area. The parent mounts it only with the switch on and a patient. */
export function AssignCarePlanAction({ patientId, className }: { patientId: string; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-block="AssignCarePlan" className={cn('flex', className)}>
      <Button type="button" variant="secondary" size="sm" icon={<Icon name="add" size={16} color="currentColor" aria-hidden="true" />} onClick={() => setOpen(true)}>
        Assign care plan
      </Button>
      <AssignCarePlanDialog open={open} onClose={() => setOpen(false)} patientId={patientId} />
    </div>
  );
}
